/**
 * Dev seed data for the shell (moved out of app.tsx unchanged). Nothing here
 * reaches a field build:
 *
 *   SEED_DECISIONS / SEED_MASTER_ITEMS  the Decisions and Action List fallback
 *     when no endpoint is baked in and nothing is cached (a local `vite dev`)
 *   SEED_SIDECARS + fetchSidecarStub    the report detail's included items,
 *     which still have no read seam
 *   fetchPdfBytesStub                   the report PDF bytes stub
 */
import type { Decision } from "../slice/decision-slice";
import type { MasterItem } from "../components/MasterActionListView";
import type { ReportSidecar } from "../reports/report-types";

// RETIRED 2026-07-27 (task #4, T-1g-READ): SEED_DECISIONS and SEED_MASTER_ITEMS
// no longer feed any surface. Decisions and the Action List now render from the
// LIVE server read (`useReadBack` → GET /mark/{decisions,actions}), reconciled
// against in-flight local outbox changes and cached for offline. The constants
// below are kept ONLY as the DEV FALLBACK for a build with no endpoint baked in
// — see `liveDecisions`/`liveActions`, which never reach for them in a field
// build (one always has an endpoint) and never use them to mask a failed read.
// Read-back was gated (PIN-1g→1l-PWA-TRANSPORT/S8) while the seam didn't exist;
// it exists now, on both sides.
//
// SEED_REPORTS/SEED_SIDECARS DO still feed the Reports tab: `report_digest` is a
// library, not a REGISTERED signed skill, so it has no read seam to wire — see
// the Reports mount for the full note.
export const SEED_DECISIONS: Decision[] = [
  {
    id: "seed-d1",
    decision_id: "d-seed-1",
    text: "Primary bath tile — owner to confirm colourway before order",
    status: "open",
    first_raised_ts: "2026-06-12T00:00:00Z",
    source_refs: [],
    owner: "Design team",
    criticality_tier: "high",
    room_zone: "Primary bath",
  },
  {
    id: "seed-d2",
    decision_id: "d-seed-2",
    text: "Kitchen island overhang — 12\" vs 15\" for stool clearance",
    status: "open",
    first_raised_ts: "2026-07-12T00:00:00Z",
    source_refs: [],
    owner: "GC",
    criticality_tier: "medium",
    room_zone: "Kitchen",
  },
  {
    id: "seed-d3",
    decision_id: "d-seed-3",
    text: "Stair railing finish — matte black vs oil-rubbed bronze",
    status: "open",
    first_raised_ts: "2026-07-18T00:00:00Z",
    source_refs: [],
    owner: "Owner",
    criticality_tier: "low",
    room_zone: "Stairwell",
  },
  {
    id: "seed-d4",
    decision_id: "d-seed-4",
    text: "Garage slab — confirm no drain relocation before pour",
    status: "open",
    first_raised_ts: "2026-07-19T00:00:00Z",
    source_refs: [],
    criticality_tier: "medium",
    room_zone: "Garage",
  },
];

export const SEED_MASTER_ITEMS: MasterItem[] = [
  {
    id: "seed-m1",
    text: "Patch drywall in the great room before primer",
    source_refs: [],
    status: "can_do",
    first_raised_ts: "2026-07-05T00:00:00Z",
    trade: "Drywall",
    owner: "Miguel",
    room: "Great room",
    criticality_tier: "medium",
  },
  {
    id: "seed-m2",
    text: "Re-hang primary bath vanity light — off-centre",
    source_refs: [],
    status: "can_do",
    first_raised_ts: "2026-07-08T00:00:00Z",
    trade: "Electrical",
    owner: "Dana",
    room: "Primary bath",
    criticality_tier: "high",
  },
  {
    id: "seed-m3",
    text: "Caulk gaps at kitchen backsplash edge",
    source_refs: [],
    status: "can_do",
    first_raised_ts: "2026-07-14T00:00:00Z",
    trade: "Tile",
    owner: "Sub — TileCo",
    room: "Kitchen",
    criticality_tier: "low",
  },
];

// RETIRED 2026-07-27 (#15): SEED_REPORTS is GONE. It showed old fake reports and
// no row for today, and once filing became real (#5) tapping one would have filed
// a GENUINE report for a fixture's window. The Reports tab now renders two real
// lists: `fileableWindows()` (derived from the clock — nothing generates reports
// on a schedule, the app action IS the trigger) and the server's list of what has
// already been filed. SEED_SIDECARS survives only to back the detail screen's
// included-items view, which still has no read seam.

// Stage-A seeded sidecars (the 1i ReportModel content — included items,
// weather, sent). Keyed by sidecar_key, mirroring the outbox-v1 fixture tree.
// Kept OFF ReportEntry (a pure read-contract mirror) and fetched via
// fetchSidecarStub, exactly as fetchPdfBytesStub supplies the PDF bytes. The
// included-item `capture_id`s are demo values: opening one deep-links to the
// shared review surface, but its full entry only renders once the live read
// transport lands (S8 read-gap, task #12).
export const SEED_SIDECARS: Record<string, ReportSidecar> = {
  "tenant-a/proj-1/daily/daily-2026-07-10.json": {
    weather: "72° clear — weather auto-filled (site geofence / NOAA)",
    included: [
      { capture_id: "cap-seed-1", title: "Shower curb waterproofing — confirm before pour", meta: "Master Bath · 9:41 AM · flagged: Daily" },
      { capture_id: "cap-seed-2", title: "Foundation step-down — mark for framer", meta: "West elevation · 10:14 AM · flagged: Daily" },
      { capture_id: "cap-seed-3", title: "Site fencing — replace damaged panel", meta: "Site · 11:02 AM · flagged: Daily" },
    ],
  },
  "tenant-a/proj-1/weekly/weekly-2026-W28.json": {
    included: [
      { capture_id: "cap-seed-4", title: "Framing complete on unit B", meta: "Wed · flagged: Weekly" },
      { capture_id: "cap-seed-5", title: "Window delivery confirmed for Jul 15", meta: "Fri · flagged: Weekly" },
    ],
  },
};

// Stubbed to a local blob dir per WI-6a-1 (no live 1h-lite outbox client in
// Stage A): resolve any pdf_key to an empty local byte buffer. The live leg
// swaps this for the real outbox client with zero shell change.
export async function fetchPdfBytesStub(_pdf_key: string): Promise<Uint8Array> {
  void _pdf_key;
  return new Uint8Array(0);
}

// The sidecar counterpart of fetchPdfBytesStub — resolves a sidecar_key to its
// seeded 1i ReportModel content (Stage A). Live leg swaps this for the real
// read client with zero shell change.
export async function fetchSidecarStub(sidecar_key: string): Promise<ReportSidecar> {
  return SEED_SIDECARS[sidecar_key] ?? { included: [] };
}
